import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  registerWhatsappTemplate,
  RegisterWhatsappTemplateRequest,
} from '../services/WhatsappTemplatesCRUD';
import { toast } from 'react-toastify';

enum State {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
}

const DEFAULT_REQUEST: RegisterWhatsappTemplateRequest = {
  provider: 'dialog360',
  name: '',
  code: '',
  minHoursToResend: 1,
  isMarketing: false,
};

export default function TemplateRegistrationForm() {
  const intl = useIntl();
  const [state, setState] = useState<State>(State.IDLE);
  const [request, setRequest] =
    useState<RegisterWhatsappTemplateRequest>(DEFAULT_REQUEST);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState(State.LOADING);
    try {
      await registerWhatsappTemplate(request);
      toast.success(
        intl.formatMessage({ id: 'whatsappTemplates.templateRegistered' })
      );
      setRequest(DEFAULT_REQUEST);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    } finally {
      setState(State.IDLE);
    }
  }

  return (
    <div className="card p-10">
      <h1 className="mb-8">
        {intl.formatMessage({ id: 'whatsappTemplates.registerTemplate' })}
      </h1>
      <form className="d-flex flex-column gap-4" onSubmit={onSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="provider">
            {intl.formatMessage({ id: 'whatsappTemplates.provider' })}
          </label>
          <select
            required
            className="form-select"
            id="provider"
            value={request.provider}
            onChange={(e) =>
              setRequest({
                ...request,
                provider: e.target
                  .value as RegisterWhatsappTemplateRequest['provider'],
              })
            }
          >
            <option value="dialog360">
              {intl.formatMessage({
                id: 'whatsappTemplates.providerDialog360',
              })}
            </option>
            <option value="vixiees">
              {intl.formatMessage({ id: 'whatsappTemplates.providerVixiees' })}
            </option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="name">
            {intl.formatMessage({ id: 'whatsappTemplates.name' })}
          </label>
          <input
            required
            className="form-control"
            id="name"
            type="text"
            placeholder={intl.formatMessage({ id: 'whatsappTemplates.name' })}
            value={request.name}
            onChange={(e) => setRequest({ ...request, name: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="templateCode">
            {intl.formatMessage({ id: 'whatsappTemplates.templateCode' })}
          </label>
          <input
            required
            className="form-control"
            id="templateCode"
            type="text"
            pattern="[a-zA-Z0-9_-]+"
            placeholder={intl.formatMessage({
              id: 'whatsappTemplates.templateCode',
            })}
            value={request.code}
            onChange={(e) => setRequest({ ...request, code: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="minHoursToResend">
            {intl.formatMessage({ id: 'whatsappTemplates.minHoursToResend' })}
          </label>
          <input
            required
            className="form-control"
            id="minHoursToResend"
            type="number"
            min={0}
            placeholder={intl.formatMessage({
              id: 'whatsappTemplates.minHoursToResend',
            })}
            value={request.minHoursToResend}
            onChange={(e) =>
              setRequest({ ...request, minHoursToResend: +e.target.value })
            }
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="isMarketing">
            {intl.formatMessage({ id: 'whatsappTemplates.isMarketing' })}
          </label>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              id="isMarketing"
              type="checkbox"
              checked={request.isMarketing}
              onChange={(e) =>
                setRequest({ ...request, isMarketing: e.target.checked })
              }
            />
          </div>
        </div>
        <button
          className="btn btn-primary mt-4 ms-auto"
          type="submit"
          disabled={state === State.LOADING}
        >
          {state === State.LOADING && (
            <span className="spinner-border spinner-border-sm me-1"></span>
          )}
          {intl.formatMessage({ id: 'whatsappTemplates.register' })}
        </button>
      </form>
    </div>
  );
}
